import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { publicAPI } from '../../services/api';

const DisplayPageSimple = () => {
  // Simple state management
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedIds, setProcessedIds] = useState(new Set()); // Track processed queue IDs
  const [sessionId] = useState(() => Date.now()); // Unique session ID

  const audioRef = useRef(null);
  const isProcessingRef = useRef(false); // Global processing lock
  const queueCountRef = useRef(0);
  const currentAnnouncementRef = useRef(null);
  const isPlayingRef = useRef(false);
  const playingQueueIdRef = useRef(null); // Track which queue ID is currently playing
  const scheduledQueueIds = useRef(new Set()); // Track which queue IDs have been scheduled for play

  // Fetch the next announcement from queue
  const fetchNextAnnouncement = async () => {
    // Prevent concurrent fetches
    if (isProcessingRef.current) {
      console.log('⚠️ Already fetching, skipping duplicate request');
      return;
    }
    
    isProcessingRef.current = true;
    
    try {
      setError(null);
      console.log(`📡 [Session ${sessionId}] Fetching next announcement from queue...`);
      
      const response = await publicAPI.getQueueEntries({ limit: 1 });
      const data = response.data.data;
      
      setQueueCount(data.count);
      queueCountRef.current = data.count; // Keep ref in sync
      setLastUpdate(new Date());
      
      if (data.announcements && data.announcements.length > 0) {
        // Filter out already processed announcements
        const unprocessedAnnouncements = data.announcements.filter(ann => !processedIds.has(ann.queueId));
        
        if (unprocessedAnnouncements.length > 0) {
          const nextAnnouncement = unprocessedAnnouncements[0];
          console.log('🎵 Got next announcement:', {
            queueId: nextAnnouncement.queueId,
            submissionId: nextAnnouncement.submissionId,
            title: nextAnnouncement.title,
            playOrder: nextAnnouncement.playOrder, // Will always be 1 now
            audioUrl: nextAnnouncement.audio?.fileUrl
          });
          console.log('📊 Current processed IDs:', Array.from(processedIds));
          
          // Mark as being processed
          setProcessedIds(prev => new Set([...prev, nextAnnouncement.queueId]));
          setCurrentAnnouncement(nextAnnouncement);
          currentAnnouncementRef.current = nextAnnouncement; // Keep ref in sync
          
          // Auto-play the announcement if it has audio (with longer delay for demo)
          if (nextAnnouncement.audio?.fileUrl) {
            // Prevent duplicate scheduling of the same queue ID
            if (scheduledQueueIds.current.has(nextAnnouncement.queueId)) {
              console.log(`⚠️ [DUPLICATE SCHEDULE] Queue ID ${nextAnnouncement.queueId} already scheduled, skipping`);
              return;
            }
            
            scheduledQueueIds.current.add(nextAnnouncement.queueId);
            console.log(`⏰ [SCHEDULE] Scheduling play for Queue ID ${nextAnnouncement.queueId} in 3 seconds...`);
            const timeoutId = setTimeout(() => {
              console.log(`🎬 [TIMEOUT FIRED] Attempting to play Queue ID ${nextAnnouncement.queueId}...`);
              playAnnouncement(nextAnnouncement);
            }, 3000); // Increased delay to 3 seconds for better visibility
            console.log(`⏰ [SCHEDULE] Timeout ID ${timeoutId} created for Queue ID ${nextAnnouncement.queueId}`);
          } else {
            console.warn('⚠️ Announcement has no audio, marking as completed');
            setTimeout(() => {
              markAsCompletedAndNext(nextAnnouncement);
            }, 3000); // Show text for 3 seconds then complete
          }
        } else {
          console.log('📭 All announcements already processed in this session');
          setCurrentAnnouncement(null);
          currentAnnouncementRef.current = null; // Keep ref in sync
        }
      } else {
        console.log('📭 No announcements in queue');
        setCurrentAnnouncement(null);
        currentAnnouncementRef.current = null; // Keep ref in sync
      }
      
    } catch (error) {
      console.error('❌ Error fetching queue:', error);
      setError('Failed to fetch announcements');
    } finally {
      isProcessingRef.current = false; // Always release the lock
    }
  };

  // Play an announcement
  const playAnnouncement = async (announcement) => {
    if (!announcement?.audio?.fileUrl || isProcessing) {
      console.log('❌ Cannot play: no audio or already processing');
      return;
    }

    // Prevent duplicate plays of the same queue entry
    if (playingQueueIdRef.current === announcement.queueId) {
      console.log(`⚠️ [DUPLICATE PLAY] Queue ID ${announcement.queueId} is already playing, skipping duplicate play`);
      return;
    }

    // Also check if this queue ID was already processed
    if (processedIds.has(announcement.queueId)) {
      console.log(`⚠️ [ALREADY PROCESSED] Queue ID ${announcement.queueId} was already processed, skipping`);
      return;
    }

    try {
      setIsProcessing(true);
      setIsPlaying(true);
      isPlayingRef.current = true; // Keep ref in sync
      playingQueueIdRef.current = announcement.queueId; // Lock this queue ID
      
      console.log(`🎵 [PLAY START] Queue ID: ${announcement.queueId}, Submission: ${announcement.submissionId}, Title: ${announcement.title}`);
      console.log(`🎵 [PLAY START] Audio URL: ${announcement.audio.fileUrl}`);

      // Set up audio - ensure clean state first
      if (audioRef.current) {
        console.log(`🔧 [AUDIO SETUP] Resetting audio element for Queue ID ${announcement.queueId}`);
        
        // Stop any existing playback
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        
        // Set new source
        audioRef.current.src = announcement.audio.fileUrl;
        audioRef.current.load();
        
        console.log(`🔧 [AUDIO SETUP] Audio source set to: ${announcement.audio.fileUrl}`);
        
        // Wait for audio to be ready
        await new Promise((resolve, reject) => {
          const handleCanPlay = () => {
            audioRef.current.removeEventListener('canplay', handleCanPlay);
            audioRef.current.removeEventListener('error', handleError);
            resolve();
          };
          
          const handleError = (e) => {
            audioRef.current.removeEventListener('canplay', handleCanPlay);
            audioRef.current.removeEventListener('error', handleError);
            reject(new Error('Audio loading failed'));
          };
          
          audioRef.current.addEventListener('canplay', handleCanPlay);
          audioRef.current.addEventListener('error', handleError);
          
          // Fallback timeout
          setTimeout(() => {
            audioRef.current.removeEventListener('canplay', handleCanPlay);
            audioRef.current.removeEventListener('error', handleError);
            resolve(); // Continue anyway
          }, 3000);
        });

        // Play the audio
        console.log(`▶️ [AUDIO PLAY] Starting playback for Queue ID ${announcement.queueId}`);
        await audioRef.current.play();
        console.log(`▶️ [AUDIO PLAY] Play() method called successfully for Queue ID ${announcement.queueId}`);
        
        // Wait for audio to finish
        await new Promise((resolve) => {
          const handleEnded = () => {
            console.log(`🏁 [AUDIO END] Audio ended naturally for Queue ID ${announcement.queueId}`);
            audioRef.current.removeEventListener('ended', handleEnded);
            audioRef.current.removeEventListener('error', handleError);
            resolve();
          };
          
          const handleError = (e) => {
            console.error(`❌ [AUDIO ERROR] Playback error for Queue ID ${announcement.queueId}:`, e);
            audioRef.current.removeEventListener('ended', handleEnded);
            audioRef.current.removeEventListener('error', handleError);
            resolve(); // Continue anyway
          };
          
          console.log(`⏳ [AUDIO WAIT] Waiting for audio to finish for Queue ID ${announcement.queueId}`);
          audioRef.current.addEventListener('ended', handleEnded);
          audioRef.current.addEventListener('error', handleError);
        });

        console.log(`✅ [PLAY END] Queue ID: ${announcement.queueId} finished playing`);
      }

    } catch (error) {
      console.error('❌ Error playing announcement:', error);
    } finally {
      setIsPlaying(false);
      isPlayingRef.current = false; // Keep ref in sync
      playingQueueIdRef.current = null; // Release the queue ID lock
      scheduledQueueIds.current.delete(announcement.queueId); // Remove from scheduled set
      
      // Mark as completed and move to next
      await markAsCompletedAndNext(announcement);
    }
  };

  // Mark announcement as completed and fetch next
  const markAsCompletedAndNext = async (announcement) => {
    // Prevent duplicate completion attempts
    if (isProcessing) {
      console.log('⚠️ Already processing, skipping duplicate completion attempt');
      return;
    }
    
    try {
      console.log(`📝 [MARK COMPLETE] Queue ID: ${announcement.queueId}, Submission: ${announcement.submissionId}`);
      
      await publicAPI.markQueueEntryCompleted(announcement.queueId);
      console.log(`✅ Queue entry ${announcement.queueId} marked as completed`);
      
      // Show completion message briefly
      setTimeout(() => {
        setCurrentAnnouncement(null);
        currentAnnouncementRef.current = null; // Keep ref in sync
        setIsProcessing(false);
        
        // Fetch next announcement after a short delay
        setTimeout(() => {
          fetchNextAnnouncement();
        }, 2000);
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error marking as completed:', error);
      
      // Check if it's already completed (not an error we should show to user)
      if (error.response?.status === 400 && error.response?.data?.message?.includes('not pending')) {
        console.log('ℹ️ Queue entry was already completed, continuing...');
        // Don't set error state for this case
      } else {
        console.error('❌ Unexpected error:', error.response?.data);
        setError(`Failed to mark announcement as completed: ${error.response?.data?.message || error.message}`);
      }
      
      setIsProcessing(false);
      
      // Still try to fetch next announcement
      setTimeout(() => {
        fetchNextAnnouncement();
      }, 1000); // Shorter delay for error cases
    }
  };

  // Manual refresh with complete state reset
  const handleRefresh = () => {
    if (!isProcessing) {
      console.log('🔄 Complete state reset initiated...');
      
      // Clear all state
      setError(null);
      setCurrentAnnouncement(null);
      setIsPlaying(false);
      setQueueCount(0);
      setProcessedIds(new Set());
      
      // Clear all refs
      currentAnnouncementRef.current = null;
      isPlayingRef.current = false;
      queueCountRef.current = 0;
      isProcessingRef.current = false;
      playingQueueIdRef.current = null; // Clear playing queue ID lock
      scheduledQueueIds.current = new Set(); // Clear scheduled queue IDs
      
      // Stop any playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = '';
      }
      
      console.log('✅ State reset complete, fetching fresh data...');
      fetchNextAnnouncement();
    }
  };

  // Initial load and periodic refresh
  useEffect(() => {
    fetchNextAnnouncement();
    
    // Poll for new announcements every 15 seconds (only if completely idle)
    const interval = setInterval(() => {
      // Use refs to get current state values (avoid stale closures)
      const currentQueue = queueCountRef.current;
      const currentlyProcessing = isProcessingRef.current;
      const hasCurrentAnnouncement = !!currentAnnouncementRef.current;
      const currentlyPlaying = isPlayingRef.current;
      
      // Only fetch if system is completely idle - no queue, no processing, no playing
      if (currentQueue === 0 && !currentlyProcessing && !hasCurrentAnnouncement && !currentlyPlaying) {
        console.log('🔄 Periodic check for new announcements...');
        fetchNextAnnouncement();
      } else {
        console.log('⏸️ Skipping periodic check - system is active (queue:', currentQueue, 'processing:', currentlyProcessing, 'current:', hasCurrentAnnouncement, 'playing:', currentlyPlaying, ')');
      }
    }, 15000);
    
    return () => clearInterval(interval);
  }, []); // Empty dependency array to prevent re-creation

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="container mx-auto px-6 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
            🔊 Voice Announcements
          </h1>
          <p className="text-xl text-blue-200">Live Announcement Display System</p>
        </div>

        {/* Status Bar */}
        <div className="flex justify-between items-center mb-8 text-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span>System Online</span>
            </div>
            <div className="flex items-center gap-2">
              <span>⏰</span>
              <span>Last Update: {format(lastUpdate, 'HH:mm:ss')}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4">
              <span>Queue: {queueCount}</span>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isProcessing}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded-lg transition-all"
            >
              <RefreshCw className={`w-4 h-4 ${isProcessing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-all text-sm"
            >
              🔄 Force Reset
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-8 text-center">
            <div className="text-red-300 mb-2">⚠️ Connection Error</div>
            <div className="text-sm text-red-200">{error}</div>
          </div>
        )}

        {/* Current Announcement */}
        {currentAnnouncement ? (
          <div className={`bg-white/10 backdrop-blur-lg rounded-3xl p-8 text-center border border-white/20 transition-all duration-500 ${
            isPlaying ? 'bg-green-500/20 border-green-400/50 scale-105 shadow-2xl' : ''
          }`}>
            
            {/* Announcement Text */}
            <div className="text-3xl md:text-4xl font-light mb-8 leading-relaxed">
              {currentAnnouncement.text}
            </div>
            
            {/* Translated Text (if available) */}
            {currentAnnouncement.translatedText && currentAnnouncement.translatedText !== currentAnnouncement.text && (
              <div className="text-2xl md:text-3xl font-light mb-8 leading-relaxed text-blue-200 border-t border-white/20 pt-6">
                {currentAnnouncement.translatedText}
              </div>
            )}
            
            {/* Status */}
            {isProcessing && !isPlaying ? (
              <div className="text-2xl text-green-300 mb-6 animate-pulse">
                ✓ Announcement Complete
              </div>
            ) : (
              <div className="text-xl text-blue-300 mb-6">
                {isPlaying ? '🔊 Now Playing' : '⏸️ Ready to Play'}
                <span className="ml-4 text-lg">
                  (Play {currentAnnouncement.playOrder})
                </span>
              </div>
            )}
            
            {/* Metadata */}
            <div className="flex justify-center flex-wrap gap-6 mb-8 text-sm opacity-90">
              <div className="flex items-center gap-2">
                <span>🎯</span>
                <span>{currentAnnouncement.category || 'General'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span>⏰</span>
                <span>{format(new Date(currentAnnouncement.approvedAt), 'HH:mm:ss')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span>🎵</span>
                <span>{currentAnnouncement.audio?.voice || 'Default Voice'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span>🆔</span>
                <span>Queue #{currentAnnouncement.queueId}</span>
              </div>
            </div>

            {/* Manual Controls */}
            <div className="flex justify-center gap-4">
              <button
                onClick={() => playAnnouncement(currentAnnouncement)}
                disabled={!currentAnnouncement.audio || isProcessing}
                className="flex items-center gap-2 px-8 py-4 bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-all backdrop-blur-lg border border-white/30 text-lg"
              >
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                {isPlaying ? 'Playing...' : 'Play Again'}
              </button>
            </div>
          </div>
        ) : (
          /* No Announcements */
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 text-center border border-white/20">
            <div className="text-6xl mb-6">🎵</div>
            <div className="text-2xl font-light mb-4">
              {isProcessing ? 'Preparing Next Announcement...' : 'No Announcements in Queue'}
            </div>
            <div className="text-blue-200">
              {isProcessing ? 'Please wait...' : 'Waiting for new announcements...'}
            </div>
            {isProcessing && (
              <div className="mt-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
              </div>
            )}
          </div>
        )}

        {/* Queue Status */}
        <div className="mt-8 text-center">
          <div className="text-2xl font-bold mb-2">Upcoming Announcements</div>
          <div className="text-6xl font-bold text-blue-300">{queueCount}</div>
          <div className="text-blue-200">in queue</div>
        </div>
      </div>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        crossOrigin="anonymous"
        preload="auto"
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default DisplayPageSimple;
